// SR-detail consult panel (nsofar docs/33) — HCM's second console over
// nsofar's doctor-consult lifecycle.
//
// Source-of-truth rule: every button here RELAYS a consult.* action through
// apps/api → nsofar's webhook; nothing mutates locally. After a successful
// send the panel shows "awaiting nsofar sync" and the block refreshes when
// /sync/sr-update lands. A 409 already_confirmed (the §5.4 two-console race)
// renders as an informative banner, not a failure.

import { useState, type JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import { CONSULT_ACTIONS_BY_STATUS, type ConsultBlock, type ConsultStatus } from "@hcm/core";
import { apiFetch } from "../../lib/api.js";
import { type ConsultActionErrorBody } from "../../lib/consultOps.js";
import { ConsultStateBadge } from "./ConsultStateBadge.js";
import { ConsultInfoRows, ConsultActions, ConsultOptionsSection, useConsultAct } from "./ConsultPanelSections.js";

interface ConsultSr { _id: string; nsofar_sr_id?: string | null; consult?: ConsultBlock | null }

function RaceBanner({ race, onDismiss }: { race: ConsultActionErrorBody; onDismiss: () => void }): JSX.Element {
  return (
    <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-sm text-amber-900" role="alert">
      <p className="font-medium">Already confirmed on another console</p>
      <p>{race.message ?? "This consult was selected elsewhere first."} The panel will update from nsofar sync.</p>
      <button type="button" className="text-xs underline mt-1" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}

function HeaderChips({ consult }: { consult: ConsultBlock }): JSX.Element {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <h2 className="text-sm font-semibold text-slate-700">Doctor Consult (nsofar)</h2>
      <ConsultStateBadge status={consult.status} />
      {consult.auto_confirm && <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">auto-confirm</span>}
      {consult.entitlement && (
        <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
          {consult.entitlement.outcome === "included"
            ? `Included${consult.entitlement.plan_key ? ` · ${consult.entitlement.plan_key}` : ""}`
            : "À la carte"}
        </span>
      )}
    </div>
  );
}

export function ConsultPanel({ sr, onChanged }: { sr: ConsultSr; onChanged: () => void }): JSX.Element | null {
  const consult = sr.consult;
  const [race, setRace] = useState<ConsultActionErrorBody | null>(null);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [formEpoch, setFormEpoch] = useState(0); // remounts ConsultActions → closes open forms after a successful send

  const { data: cfg } = useQuery<{ actions_enabled: boolean }>({
    queryKey: ["consult-ops-config"],
    queryFn: () => apiFetch<{ actions_enabled: boolean }>("/consult-ops/config"),
    staleTime: 5 * 60 * 1000,
  });
  const actionsEnabled = cfg?.actions_enabled === true;

  const act = useConsultAct(sr._id, {
    onPaymentLink: setPaymentLink,
    onRace: (body) => { setRace(body); onChanged(); },
    onSent: () => { setFormEpoch((n) => n + 1); onChanged(); },
  });

  if (!consult) return null;
  const verbs = CONSULT_ACTIONS_BY_STATUS[consult.status as ConsultStatus] ?? [];

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3" data-testid="consult-panel">
      <HeaderChips consult={consult} />
      <ConsultInfoRows srId={sr._id} nsofarSrId={sr.nsofar_sr_id} consult={consult} paymentLink={paymentLink} />
      <ConsultOptionsSection
        consult={consult}
        selectable={actionsEnabled && verbs.includes("select_on_behalf")}
        pending={act.isPending}
        onSelect={(optionId) => act.mutate({ path: "select", body: { option_id: optionId } })}
      />

      {race && <RaceBanner race={race} onDismiss={() => setRace(null)} />}
      <ConsultActions key={formEpoch} verbs={verbs} enabled={actionsEnabled} pending={act.isPending}
        onSend={(path, body) => act.mutate({ path, body })} />
    </div>
  );
}
